// =============================================================================
// HrEquipmentScreen — /hr/equipment  (manager+; supervisors can allocate too)
//
// 22 Oct 2026 (mig 444) — James: "I want to still see the registers in HR as
// managers to see, track, assign and monitor equipment, with visual widgets
// or graphs highlighting upcoming." Rigging, power tools and ladders left the
// Assets tabs (decision 1 — vehicles, IT, insurance stay there) and live here.
//
// Top: the numbers that need a hand — each tile filters the table when tapped.
// Then: where the gear stands by kit (one bar per kit, coloured by standing)
// and what falls due over the next three months. Then the work: open requests
// (one-tap allocate / decline), pending approvals, the kits panel (new kit,
// move a kit to a vehicle). Then the register table with search, chips and
// Export (the road-week Excel / PDF, on the rows you can see or the rows you
// ticked).
//
// Everything writes through the mig-444 RPCs (equipment_allocate / approve /
// request_decline); kits write straight to equipment_kits (manager+ RLS).
// =============================================================================

import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, Download, Plus, Truck, Warehouse, Hand, CheckCircle2, X, UserCheck,
  AlertTriangle, Clock, HelpCircle, ShieldOff, Wrench, ClipboardList, Package,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useStickyState } from "@/lib/stickyState";   // 29 Oct 2026 — filters survive back / refresh / rotate
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, Field, Input } from "@/components/ui";
import {
  type RegisterRow, type Kind, type Urgency, type Kit,
  KIND_LABEL, KIND_PLURAL, URGENCY_LABEL, URGENCY_TONE, URGENCY_TEXT, URGENCY_ORDER,
  TagChip, fmtDay, daysUntil, itemHref, inspectHref,
  useEquipmentRegister, useEquipmentKits, useOpenRequests, useEquipmentActions,
  ExportDialog, AllocateDialog, ReasonSheet,
} from "./equipmentShared";

type Focus = "all" | Urgency | "pending" | "defects" | "unallocated";

const URGENCY_BAR: Record<Urgency, string> = {
  out_of_service: "bg-danger", overdue: "bg-danger/70", due_soon: "bg-warn", never_inspected: "bg-info", current: "bg-ok",
};

function monthKey(key: string) { return key.slice(0, 7); }
function monthLabel(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
}
function nextMonths(n: number): string[] {
  const d = new Date(); const out: string[] = [];
  for (let i = 0; i < n; i++) { const x = new Date(d.getFullYear(), d.getMonth() + i, 1); out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); }
  return out;
}

export default function HrEquipmentScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canAllocate = hasRole(user, "supervisor");
  const isManager   = hasRole(user, "manager");
  const me = user?.employee_id ?? null;

  const [focus, setFocus]   = useStickyState<Focus>("hreq:focus", "all", (params.get("focus") as Focus) || undefined);
  const [kind, setKind]     = useStickyState<Kind | "all">("hreq:kind", "all", (params.get("kind") as Kind) || undefined);
  const [kitId, setKitId]   = useStickyState<string>("hreq:kit", "all", params.get("kit") ?? undefined);
  const [holder, setHolder] = useStickyState<string>("hreq:holder", "all");
  const [search, setSearch] = useStickyState("hreq:search", "");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [allocating, setAllocating] = useState<RegisterRow | null>(null);
  const [declining, setDeclining]   = useState<string | null>(null);
  const [newKitOpen, setNewKitOpen] = useState(false);

  const { data: rows = [], isLoading } = useEquipmentRegister(!!user);
  const { data: kits = [] } = useEquipmentKits(!!user);
  const { data: requests = [] } = useOpenRequests(!!user);
  const { data: employees = [] } = useQuery({
    queryKey: ["equipment_employees"],
    queryFn: async () => {
      const { data, error } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; first_name: string | null; last_name: string | null }[];
    },
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["equipment_vehicles"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("vehicles").select("vehicle_id, vehicle_code, make, model, rego, assigned_to_employee_id").order("vehicle_code");
      if (error) throw error;
      return (data ?? []) as { vehicle_id: string; vehicle_code: string; make: string | null; model: string | null; rego: string | null; assigned_to_employee_id: string | null }[];
    },
  });
  const { allocate, approve, decline } = useEquipmentActions();
  const empName = (id: string | null) => { const e = employees.find(x => x.employee_id === id); return e ? [e.first_name, e.last_name].filter(Boolean).join(" ") : "—"; };

  // ---- numbers -------------------------------------------------------------
  const counts = useMemo(() => {
    const c = { out_of_service: 0, overdue: 0, due_soon: 0, never_inspected: 0, current: 0, pending: 0, defects: 0, unallocated: 0, total: rows.length };
    for (const r of rows) {
      c[r.urgency]++;
      if (r.registration_status === "pending") c.pending++;
      if (r.open_defects > 0) c.defects++;
      if (!r.holder_employee_id && !r.kit_id && !r.kept_in_vehicle_code) c.unallocated++;
    }
    return c;
  }, [rows]);

  const byKit = useMemo(() => {
    const m = new Map<string, { name: string; vehicle: string | null; rows: RegisterRow[] }>();
    for (const r of rows) {
      const key = r.kit_id ?? (r.kept_in_vehicle_code ? `veh:${r.kept_in_vehicle_code}` : "none");
      const name = r.kit_name ?? (r.kept_in_vehicle_code ? `In ${r.kept_in_vehicle_code}` : "No kit");
      if (!m.has(key)) m.set(key, { name, vehicle: r.kit_vehicle_code ?? r.kept_in_vehicle_code, rows: [] });
      m.get(key)!.rows.push(r);
    }
    return [...m.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.rows.length - a.rows.length);
  }, [rows]);

  const months = nextMonths(3);
  const dueByMonth = useMemo(() => months.map(ym => rows.filter(r => r.next_due && monthKey(r.next_due) === ym && r.urgency !== "out_of_service").length), [rows, months]);
  const dueMax = Math.max(1, ...dueByMonth, counts.overdue);

  // ---- table ---------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (kind !== "all" && r.kind !== kind) return false;
      if (kitId === "depot" ? (r.kit_id || r.kept_in_vehicle_code) : kitId !== "all" && r.kit_id !== kitId) return false;
      if (holder === "none" ? r.holder_employee_id : holder !== "all" && r.holder_employee_id !== holder) return false;
      if (focus === "pending" && r.registration_status !== "pending") return false;
      if (focus === "defects" && r.open_defects === 0) return false;
      if (focus === "unallocated" && (r.holder_employee_id || r.kit_id || r.kept_in_vehicle_code)) return false;
      if (focus !== "all" && focus !== "pending" && focus !== "defects" && focus !== "unallocated" && r.urgency !== focus) return false;
      if (q && ![r.code, r.asset_tag, r.serial_number, r.display_name, r.make, r.model, r.kit_name, r.holder_name, r.kit_vehicle_code, r.kept_in_vehicle_code].some(v => (v ?? "").toLowerCase().includes(q))) return false;
      return true;
    }).sort((a, b) => URGENCY_ORDER.indexOf(a.urgency) - URGENCY_ORDER.indexOf(b.urgency) || (a.next_due ?? "9").localeCompare(b.next_due ?? "9") || a.code.localeCompare(b.code));
  }, [rows, kind, kitId, holder, focus, search]);

  const rowKey = (r: RegisterRow) => `${r.kind}:${r.item_id}`;
  const exportRows = ticked.size > 0 ? filtered.filter(r => ticked.has(rowKey(r))) : filtered;
  const pending = rows.filter(r => r.registration_status === "pending");
  const setFocusAndUrl = (f: Focus) => { setFocus(f === focus ? "all" : f); const p = new URLSearchParams(params); if (f === focus) p.delete("focus"); else p.set("focus", f); setParams(p, { replace: true }); };

  const refresh = () => { qc.invalidateQueries({ queryKey: ["equipment_register"] }); qc.invalidateQueries({ queryKey: ["equipment_requests"] }); qc.invalidateQueries({ queryKey: ["equipment_kits"] }); };

  // kits
  const createKit = useMutation({
    mutationFn: async (v: { kit_name: string; vehicle_id: string | null }) => {
      const { error } = await (supabase as any).from("equipment_kits").insert({ kit_name: v.kit_name, vehicle_id: v.vehicle_id });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setNewKitOpen(false); refresh(); },
    onError: (e: any) => window.alert(e?.message ?? "Could not create the kit"),
  });
  const moveKit = useMutation({
    mutationFn: async (v: { kit_id: string; vehicle_id: string | null }) => {
      const { error } = await (supabase as any).from("equipment_kits").update({ vehicle_id: v.vehicle_id }).eq("kit_id", v.kit_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
    onError: (e: any) => window.alert(e?.message ?? "Could not move the kit"),
  });

  if (user && !canAllocate) return <Navigate to="/equipment" replace/>;

  const Tile = ({ k, label, value, icon: Icon, tone }: { k: Focus; label: string; value: number; icon: typeof Clock; tone: string }) => (
    <button type="button" onClick={() => setFocusAndUrl(k)}
      className={`text-left rounded-lg border p-3 transition-colors ${focus === k ? "border-accent bg-accent/8" : "border-line bg-ink-card hover:border-accent/40"}`}>
      <div className="flex items-center justify-between"><span className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">{label}</span><Icon size={13} className={value > 0 ? tone : "text-text-dim"}/></div>
      <div className={`text-[26px] font-semibold tracking-tight mt-1 ${value > 0 ? tone : "text-text-dim"}`}>{value}</div>
    </button>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-24">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/hr" className="text-text-muted text-[12px] hover:text-text">HR</Link>
          <h1 className="text-2xl font-semibold tracking-tight">Equipment</h1>
          <div className="text-text-muted text-[13px] mt-1">Rigging, power tools and ladders — {counts.total} items across {kits.length} kit{kits.length === 1 ? "" : "s"}. Tap a number to see those items.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/inspections/add/rigging"><Button variant="secondary" icon={Plus} size="sm">Rigging</Button></Link>
          <Link to="/inspections/add/power-tool"><Button variant="secondary" icon={Plus} size="sm">Power tool</Button></Link>
          <Link to="/assets?tab=ladders"><Button variant="secondary" icon={Plus} size="sm">Ladder</Button></Link>
          <Link to="/inspections/new"><Button variant="primary" icon={ClipboardList} size="sm">Log inspection</Button></Link>
        </div>
      </div>

      {isLoading ? <div className="py-16 flex justify-center"><Spinner/></div> : (
        <>
          {/* numbers */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
            <Tile k="out_of_service" label="Out of service" value={counts.out_of_service} icon={ShieldOff} tone="text-danger"/>
            <Tile k="overdue" label="Overdue" value={counts.overdue} icon={AlertTriangle} tone="text-danger"/>
            <Tile k="due_soon" label="Due in 30 d" value={counts.due_soon} icon={Clock} tone="text-warn"/>
            <Tile k="never_inspected" label="Never inspected" value={counts.never_inspected} icon={HelpCircle} tone="text-info"/>
            <Tile k="defects" label="Open defects" value={counts.defects} icon={Wrench} tone="text-danger"/>
            <Tile k="pending" label="Awaiting approval" value={counts.pending} icon={CheckCircle2} tone="text-accent"/>
            <Tile k="unallocated" label="Nowhere" value={counts.unallocated} icon={Package} tone="text-warn"/>
          </div>

          {/* charts */}
          <div className="grid lg:grid-cols-3 gap-3 mb-5">
            <Card className="lg:col-span-2">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">Where it stands, by kit</div>
                  <div className="flex items-center gap-3 text-[10px] text-text-muted">
                    {URGENCY_ORDER.map(u => <span key={u} className="inline-flex items-center gap-1"><i className={`w-2 h-2 rounded-sm ${URGENCY_BAR[u]}`}/>{URGENCY_LABEL[u]}</span>)}
                  </div>
                </div>
                <div className="space-y-2">
                  {byKit.slice(0, 12).map(k => {
                    const n = k.rows.length;
                    return (
                      <button key={k.key} type="button" onClick={() => setKitId(k.key.startsWith("veh:") || k.key === "none" ? "all" : (kitId === k.key ? "all" : k.key))}
                        className="w-full text-left group">
                        <div className="flex items-center justify-between text-[12px] mb-1">
                          <span className="flex items-center gap-1.5 truncate">{k.vehicle ? <Truck size={12} className="text-text-muted"/> : <Warehouse size={12} className="text-text-muted"/>}<span className={`truncate ${kitId === k.key ? "text-accent" : "text-text"}`}>{k.name}</span>{k.vehicle && <span className="text-text-dim font-mono text-[10px]">{k.vehicle}</span>}</span>
                          <span className="text-text-muted font-mono text-[11px]">{n}</span>
                        </div>
                        <div className="h-2.5 rounded-sm overflow-hidden flex bg-ink-raised">
                          {URGENCY_ORDER.map(u => { const c = k.rows.filter(r => r.urgency === u).length; return c ? <div key={u} className={URGENCY_BAR[u]} style={{ width: `${(c / n) * 100}%` }} title={`${c} ${URGENCY_LABEL[u].toLowerCase()}`}/> : null; })}
                        </div>
                      </button>
                    );
                  })}
                  {byKit.length === 0 && <div className="text-[12px] text-text-muted">Nothing on the register yet.</div>}
                </div>
              </div>
            </Card>
            <Card>
              <div className="p-4 h-full flex flex-col">
                <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-3">Coming due</div>
                <div className="flex-1 flex items-end gap-3 min-h-[120px]">
                  {[{ label: "Overdue", n: counts.overdue, cls: "bg-danger/70" }, ...months.map((ym, i) => ({ label: monthLabel(ym), n: dueByMonth[i], cls: i === 0 ? "bg-warn" : "bg-accent/60" }))].map(b => (
                    <div key={b.label} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`text-[13px] font-semibold ${b.n ? "text-text" : "text-text-dim"}`}>{b.n}</div>
                      <div className="w-full rounded-t-sm bg-ink-raised relative" style={{ height: 90 }}>
                        <div className={`absolute bottom-0 inset-x-0 rounded-t-sm ${b.cls}`} style={{ height: `${(b.n / dueMax) * 100}%` }}/>
                      </div>
                      <div className="text-[10px] text-text-muted">{b.label}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-text-dim mt-3">Inspections falling due, by month. Out-of-service gear isn't counted — it needs a pass first.</div>
              </div>
            </Card>
          </div>

          {/* work */}
          {(requests.length > 0 || pending.length > 0) && (
            <div className="grid md:grid-cols-2 gap-3 mb-5">
              {requests.length > 0 && (
                <Card className="border-accent/40">
                  <div className="p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wider2 text-accent mb-2 flex items-center gap-1.5"><Hand size={12}/> Requests ({requests.length})</div>
                    <div className="space-y-2">
                      {requests.map(q => {
                        const r = rows.find(x => x.kind === q.kind && x.item_id === q.item_id);
                        const who = [q.requested_by?.first_name, q.requested_by?.last_name].filter(Boolean).join(" ") || "Someone";
                        const target = q.requested_for_employee_id ?? q.requested_by_employee_id;
                        return (
                          <div key={q.request_id} className="rounded border border-line p-2.5 text-[12px]">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0"><span className="font-medium">{who}</span> wants <Link to={r ? itemHref(r) : "#"} className="font-mono text-accent">{r?.code ?? q.item_id.slice(0, 8)}</Link>{r?.display_name && <span className="text-text-muted"> · {r.display_name}</span>}</div>
                              <span className="text-text-dim shrink-0">{fmtDay(q.created_at.slice(0, 10))}</span>
                            </div>
                            {q.note && <div className="text-text-muted mt-0.5">“{q.note}”</div>}
                            {r?.holder_name && <div className="text-text-dim mt-0.5">Now held by {r.holder_name}</div>}
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              {r && <Button size="sm" variant="primary" icon={UserCheck} disabled={allocate.isPending}
                                onClick={() => allocate.mutate({ kind: r.kind, item_id: r.item_id, holder_employee_id: target, kit_id: r.kit_id, reason: "Request" }, { onSuccess: refresh, onError: (e: any) => window.alert(e?.message ?? "Could not allocate") })}>
                                Allocate to {empName(target).split(" ")[0]}
                              </Button>}
                              {r && <Button size="sm" variant="secondary" onClick={() => setAllocating(r)}>Other…</Button>}
                              <Button size="sm" variant="ghost" icon={X} onClick={() => setDeclining(q.request_id)}>Decline</Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              )}
              {pending.length > 0 && (
                <Card className="border-info/40">
                  <div className="p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-2 flex items-center gap-1.5"><CheckCircle2 size={12}/> Awaiting approval ({pending.length})</div>
                    <div className="space-y-2">
                      {pending.map(r => (
                        <div key={rowKey(r)} className="rounded border border-line p-2.5 text-[12px]">
                          <div className="flex items-center gap-2"><Link to={itemHref(r)} className="font-mono text-accent">{r.code}</Link><span className="truncate">{r.display_name}</span><Pill label={KIND_LABEL[r.kind]} small/></div>
                          <div className="text-text-dim mt-0.5">Added by {r.submitted_by_name ?? "—"}{r.holder_name ? ` · holding: ${r.holder_name}` : ""}</div>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Button size="sm" variant="primary" icon={CheckCircle2} disabled={approve.isPending} onClick={() => approve.mutate({ kind: r.kind, item_id: r.item_id }, { onSuccess: refresh, onError: (e: any) => window.alert(e?.message ?? "Could not approve") })}>Approve</Button>
                            <Button size="sm" variant="secondary" icon={UserCheck} onClick={() => setAllocating(r)}>Approve + allocate</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* kits */}
          <Card className="mb-5">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">Kits — a kit lives in a vehicle, or at the depot</div>
                {isManager && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setNewKitOpen(v => !v)}>New kit</Button>}
              </div>
              {newKitOpen && <NewKitForm vehicles={vehicles} busy={createKit.isPending} onCancel={() => setNewKitOpen(false)} onSave={v => createKit.mutate(v)}/>}
              {kits.length === 0 ? <div className="text-[12px] text-text-muted">No kits yet. Make one per vehicle (“Ute 1 kit”) and one or two for the depot (“Depot spares”).</div> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {kits.map(k => {
                    const n = rows.filter(r => r.kit_id === k.kit_id).length;
                    const bad = rows.filter(r => r.kit_id === k.kit_id && r.urgency !== "current").length;
                    const lead = k.vehicle?.assigned_to_employee_id ? empName(k.vehicle.assigned_to_employee_id) : null;
                    return (
                      <div key={k.kit_id} className={`rounded border p-2.5 text-[12px] ${kitId === k.kit_id ? "border-accent" : "border-line"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <button type="button" className="font-medium truncate text-left hover:text-accent" onClick={() => setKitId(kitId === k.kit_id ? "all" : k.kit_id)}>{k.kit_name}</button>
                          <span className="text-text-muted font-mono text-[11px] shrink-0">{n} item{n === 1 ? "" : "s"}{bad ? <span className="text-warn"> · {bad} to look at</span> : null}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 text-text-muted">
                          {k.vehicle_id ? <Truck size={12}/> : <Warehouse size={12}/>}
                          {isManager ? (
                            <select value={k.vehicle_id ?? ""} onChange={e => moveKit.mutate({ kit_id: k.kit_id, vehicle_id: e.target.value || null })} className="bg-transparent text-[12px] text-text outline-none flex-1">
                              <option value="">Depot</option>
                              {vehicles.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_code}{v.rego ? ` · ${v.rego}` : ""}</option>)}
                            </select>
                          ) : <span>{k.vehicle?.vehicle_code ?? "Depot"}</span>}
                        </div>
                        {lead && <div className="text-text-dim mt-0.5 pl-[18px]">Vehicle with {lead}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* table */}
          <Card>
            <div className="p-3 border-b border-line flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 min-w-[200px] max-w-sm">
                <Search size={14} className="text-text-muted"/>
                <input placeholder="Code, serial, name, kit, holder…" value={search} onChange={e => setSearch(e.target.value)} className="bg-transparent outline-none text-text text-[13px] flex-1"/>
              </div>
              <div className="flex gap-1">
                {(["all", "rigging", "power_tool", "ladder"] as const).map(k => (
                  <button key={k} type="button" onClick={() => setKind(k)} className={`px-2.5 py-1.5 rounded-full border text-[11px] font-medium ${kind === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted"}`}>{k === "all" ? "All" : KIND_PLURAL[k]}</button>
                ))}
              </div>
              <select value={kitId} onChange={e => setKitId(e.target.value)} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text">
                <option value="all">Any kit</option><option value="depot">No kit / depot</option>
                {kits.map(k => <option key={k.kit_id} value={k.kit_id}>{k.kit_name}</option>)}
              </select>
              <select value={holder} onChange={e => setHolder(e.target.value)} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text">
                <option value="all">Any holder</option><option value="none">Nobody</option>
                {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{[e.first_name, e.last_name].filter(Boolean).join(" ")}</option>)}
              </select>
              {focus !== "all" && <button type="button" onClick={() => setFocusAndUrl(focus)} className="text-[11px] text-accent inline-flex items-center gap-1"><X size={11}/> {focus === "pending" ? "Awaiting approval" : focus === "defects" ? "Open defects" : focus === "unallocated" ? "Nowhere" : URGENCY_LABEL[focus]}</button>}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] text-text-muted">{ticked.size > 0 ? `${ticked.size} ticked` : `${filtered.length} shown`}</span>
                <Button size="sm" variant="secondary" icon={Download} onClick={() => setExporting(true)} disabled={exportRows.length === 0}>Export</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
                  <tr className="border-b border-line">
                    <th className="px-3 py-2 text-left"><input type="checkbox" className="accent-accent" checked={filtered.length > 0 && filtered.every(r => ticked.has(rowKey(r)))} onChange={e => setTicked(e.target.checked ? new Set(filtered.map(rowKey)) : new Set())}/></th>
                    <th className="px-2 py-2 text-left">Item</th>
                    <th className="px-2 py-2 text-left">Kit / vehicle</th>
                    <th className="px-2 py-2 text-left">Holder</th>
                    <th className="px-2 py-2 text-left">Tag</th>
                    <th className="px-2 py-2 text-left">Last</th>
                    <th className="px-2 py-2 text-left">Next due</th>
                    <th className="px-2 py-2 text-left">Standing</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const d = daysUntil(r.next_due);
                    return (
                      <tr key={rowKey(r)} className="border-b border-line/60 hover:bg-ink-raised/50">
                        <td className="px-3 py-2"><input type="checkbox" className="accent-accent" checked={ticked.has(rowKey(r))} onChange={e => setTicked(s => { const n = new Set(s); if (e.target.checked) n.add(rowKey(r)); else n.delete(rowKey(r)); return n; })}/></td>
                        <td className="px-2 py-2">
                          <Link to={itemHref(r)} className="font-medium hover:text-accent">{r.display_name ?? r.code}</Link>
                          <div className="text-[11px] text-text-muted font-mono">{r.code}{r.serial_number ? ` · ${r.serial_number}` : ""} <span className="font-sans">· {KIND_LABEL[r.kind]}</span>{r.registration_status === "pending" && <Pill label="pending" small tone="notice"/>}{r.open_defects > 0 && <span className="text-danger"> · {r.open_defects} defect{r.open_defects === 1 ? "" : "s"}</span>}</div>
                        </td>
                        <td className="px-2 py-2 text-text-muted">{r.kit_name ?? (r.kept_in_vehicle_code ? `In ${r.kept_in_vehicle_code}` : "—")}{r.kit_vehicle_code && <span className="text-text-dim font-mono text-[10px]"> · {r.kit_vehicle_code}</span>}</td>
                        <td className="px-2 py-2">{r.holder_name ?? <span className="text-text-dim">—</span>}</td>
                        <td className="px-2 py-2">{r.kind === "ladder" ? <span className="text-text-dim">—</span> : <TagChip colour={r.tag_colour} number={r.tag_number} out={r.urgency === "out_of_service"} size="sm"/>}</td>
                        <td className="px-2 py-2 text-text-muted whitespace-nowrap">{r.last_inspection_date ? fmtDay(r.last_inspection_date) : "—"}{r.last_result && <span className="text-text-dim"> · {r.last_result}</span>}</td>
                        <td className={`px-2 py-2 whitespace-nowrap ${URGENCY_TEXT[r.urgency]}`}>{r.next_due ? `${fmtDay(r.next_due)}${d != null ? d < 0 ? ` (${-d} d over)` : ` (${d} d)` : ""}` : "—"}</td>
                        <td className="px-2 py-2"><Pill label={URGENCY_LABEL[r.urgency]} small tone={URGENCY_TONE[r.urgency]}/></td>
                        <td className="px-2 py-2 whitespace-nowrap text-right">
                          <Link to={inspectHref(r)} className="text-[11px] text-text-muted hover:text-accent mr-2">Inspect</Link>
                          {r.registration_status === "pending" && <button type="button" className="text-[11px] text-ok hover:underline mr-2" onClick={() => approve.mutate({ kind: r.kind, item_id: r.item_id }, { onSuccess: refresh })}>Approve</button>}
                          <button type="button" className="text-[11px] text-accent hover:underline" onClick={() => setAllocating(r)}>Allocate</button>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-text-muted">Nothing matches.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {exporting && (
        <ExportDialog rows={exportRows} domainLabel={kind === "all" ? "Equipment register" : `${KIND_PLURAL[kind]} register`} defaultSite=""
          scopeNote={ticked.size > 0 ? `${ticked.size} ticked items` : `${filtered.length} items${focus !== "all" ? ` · ${focus}` : ""}${kitId !== "all" ? ` · kit filter` : ""}`}
          preparedByName={[user?.first_name, user?.last_name].filter(Boolean).join(" ")} onClose={() => setExporting(false)}/>
      )}
      {allocating && (
        <AllocateDialog row={allocating} kits={kits} employees={employees} onClose={() => setAllocating(null)}
          onDone={() => { if (allocating.registration_status === "pending") approve.mutate({ kind: allocating.kind, item_id: allocating.item_id }); setAllocating(null); refresh(); }}/>
      )}
      {declining && (
        <ReasonSheet title="Decline this request" blurb="They'll get a notification with your reason." placeholder="Why not — e.g. it's booked for the Tamborine job" cta="Decline" tone="danger" busy={decline.isPending}
          onClose={() => setDeclining(null)} onSubmit={t => decline.mutate({ request_id: declining, reason: t || undefined }, { onSuccess: () => { setDeclining(null); refresh(); }, onError: (e: any) => window.alert(e?.message ?? "Could not decline") })}/>
      )}
    </div>
  );
}

function NewKitForm({ vehicles, busy, onCancel, onSave }: {
  vehicles: { vehicle_id: string; vehicle_code: string; rego: string | null }[]; busy: boolean; onCancel: () => void; onSave: (v: { kit_name: string; vehicle_id: string | null }) => void;
}) {
  const [name, setName] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  return (
    <div className="mb-3 p-3 rounded border border-accent/30 bg-accent/5 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <Field label="Kit name"><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ute 3 kit, Depot spares"/></Field>
      <Field label="Lives in">
        <select value={vehicleId} onChange={e => setVehicleId(e.target.value)} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
          <option value="">Depot</option>
          {vehicles.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_code}{v.rego ? ` · ${v.rego}` : ""}</option>)}
        </select>
      </Field>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => onSave({ kit_name: name.trim(), vehicle_id: vehicleId || null })}>{busy ? "Saving…" : "Create"}</Button>
      </div>
    </div>
  );
}

